import { api } from '@/lib/api.js';
import { inFlightDedup } from '@/lib/inFlight.js';
import { pollOpts } from '@/lib/poll.js';

// Reads /api/tracon/pipeline (the canonical broker-pipeline endpoint;
// 15min server-cached + in-flight-deduped). Returns the raw payload
// indexed by Zoho CRM Stage string (deal_stage) for fast column
// rendering in the kanban.
//
// Pipeline item shape (from routes/tracon.routes.js buildPipelineItem):
//   { deal_id, app_number, named_insured, account_id, deal_stage,
//     stage (derived), urgency, days_until_expiration,
//     policy_expiration, proposed_effective, primary_contact,
//     aircraft, quotes: { total, carriers, visible }, pilots,
//     producer_id, producer_name (the Deals CRM producer lookup: the placement partner, "Placed through", NOT the
//     producer), producer_user_id, producer_label, producer_known, producer_basis (the policy's producer, a TRACON
//     user; deals-door-1 T19), premium, ... }
//
// MINE IS THE SERVER'S (deals-door-1 T19, PLAN-FINAL 4.4): the board asks ?scope=mine | unassigned and the server keeps
// the items whose policy names the signed-in person (or none). The browser decodes no token to decide it. Expected
// answers, handled on the page: 400 mine_unavailable (the shared login is not a person), 400 bad_request, 503
// could_not_check (the producer read failed: never an empty Mine).

// Kanban columns are keyed on the RAW CRM stage (deal_stage), not the
// derived UI `stage`. Spec §2.
export const ACTIVE_STAGES = [
  'Renewal Pending',
  'Prospect',
  'Submitted to Market',
  'Quoted',
  'Presented',
  'Binding in Progress',
  'Bound (Pending Payment)',
  'Paid',
  'Bound',
];

export const TERMINAL_STAGES = ['Expired', 'Lost', 'Cancelled'];

const SCOPE_EXPECTED = Object.freeze([
  { status: 400, code: 'mine_unavailable' }, { status: 400, code: 'bad_request' }, { status: 503, code: 'could_not_check' },
]);

export function getPipeline({ poll = false, scope = 'all' } = {}) {
  const s = scope || 'all';
  // Each URL spelled whole, so the path is a literal (the demo shim's route census, tests/demo-shim-no-network.test.js,
  // reads /api literals and fills a template hole with a demo id).
  const url = s === 'all' ? '/api/tracon/pipeline' : `/api/tracon/pipeline?scope=${encodeURIComponent(s)}`;
  return inFlightDedup(`pipeline:${s}`, () => api.get(url, { ...(pollOpts(poll) || {}), expected: SCOPE_EXPECTED }));
}

// Some Bound items represent expiring renewals (the derived stage
// becomes RENEWAL_DUE). For the kanban they still belong in the
// "Bound" column — RENEWAL_DUE urgency is a card-level signal, not
// a column-level one.
function stageForColumn(item) {
  return item.deal_stage || '';
}

export async function getPipelineIndexed({ includeClosed = false, poll = false, scope = 'all' } = {}) {
  const res = await getPipeline({ poll, scope });
  const items = res?.items || [];
  const byStage = {};
  const totals = {};
  const stages = includeClosed ? [...ACTIVE_STAGES, ...TERMINAL_STAGES] : ACTIVE_STAGES;
  for (const s of stages) {
    byStage[s] = [];
    totals[s] = 0;
  }
  for (const item of items) {
    const col = stageForColumn(item);
    if (!byStage[col]) continue;
    byStage[col].push(item);
    totals[col]++;
  }
  return {
    byStage,
    totals,
    raw: items,
    scope: res?.scope || null,
    producer: res?.producer || null,
    lastFetched: new Date().toISOString(),
  };
}
